'use client'
import React, { DragEvent, useRef, useState } from 'react'
import { Trash } from '../icons'
import clsx from 'clsx'
import { ImageCropperModal, type ImageCropOptions } from './image-cropper'

export interface CoverImageData {
  url: string
  fileName: string
  platformType?: string
  fileType?: string
  properties?: any
}

export interface CoverImageUploadProps {
  coverImage: CoverImageData | null
  onImageUploaded: (imageData: CoverImageData) => void
  onImageRemoved: () => void
  onUpload: (file: File) => Promise<CoverImageData | null>
  isMutating?: boolean
  maxFileSizeMB?: number
  allowedExtensions?: string[]
  title?: string
  subtitle?: string
  dragDropText?: string
  supportText?: string
  className?: string
  aspectRatio?: 'square' | 'landscape' | 'portrait' | 'free'
  previewHeight?: string
  rounded?: boolean
  compactMode?: boolean
  /** Si se indica, la imagen pasa por el editor de recorte/posición antes de subirse. */
  cropOptions?: ImageCropOptions
}

export function CoverImageUpload({
  coverImage,
  onImageUploaded,
  onImageRemoved,
  onUpload,
  isMutating = false,
  maxFileSizeMB = 5,
  allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'],
  title = 'Imagen de portada',
  subtitle = '',
  dragDropText = 'Seleccionar imagen',
  supportText = '',
  className = '',
  aspectRatio = 'landscape',
  previewHeight = '200px',
  rounded = false,
  compactMode = false,
  cropOptions,
}: CoverImageUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [fileToCrop, setFileToCrop] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const formatSupportText = () => {
    if (supportText) return supportText
    return `Soporta ${allowedExtensions.join(', ').toUpperCase()} hasta ${maxFileSizeMB}MB`
  }

  const getAspectRatioClass = () => {
    switch (aspectRatio) {
      case 'square':
        return 'aspect-square'
      case 'landscape':
        return 'aspect-[16/9]'
      case 'portrait':
        return 'aspect-[9/16]'
      case 'free':
        return ''
      default:
        return 'aspect-[16/9]'
    }
  }

  const getRoundedClass = () => {
    return rounded ? 'rounded-full' : 'rounded-lg'
  }

  const handleImageUpload = async (file: File) => {
    setIsUploading(true)
    try {
      const uploadedImage = await onUpload(file)
      if (uploadedImage) {
        onImageUploaded(uploadedImage)
      }
    } catch (error) {
      console.error('Error uploading image:', error)
    } finally {
      setIsUploading(false)
    }
  }

  // Con editor activo, el fichero elegido se recorta antes de subirse
  const handleFileChosen = async (file: File) => {
    if (cropOptions) {
      setFileToCrop(file)
      return
    }
    await handleImageUpload(file)
  }

  const handleCropConfirm = async (croppedFile: File) => {
    setFileToCrop(null)
    await handleImageUpload(croppedFile)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0 && droppedFiles[0].type.startsWith('image/')) {
      await handleFileChosen(droppedFiles[0])
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      await handleFileChosen(selectedFile)
    }
    e.target.value = ''
  }

  const handleSelectImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleRemoveImage = () => {
    onImageRemoved()
  }

  return (
    <div className={clsx('space-y-4', className)}>
      {!compactMode && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
      )}

      {compactMode && title && (
        <div className="mb-2">
          <p className="text-sm font-medium text-gray-700">{title}</p>
        </div>
      )}

      {coverImage ? (
        // Vista previa de la imagen
        <div className="relative flex justify-center">
          <div
            className={clsx(
              'relative overflow-hidden border border-gray-200 bg-gray-50',
              getRoundedClass(),
              aspectRatio !== 'free' && getAspectRatioClass(),
              // Centrar el contenedor cuando es redondo
              rounded && 'mx-auto'
            )}
            style={
              aspectRatio === 'free'
                ? {
                    height: previewHeight,
                    width: rounded ? previewHeight : 'auto', // Hacer cuadrado cuando es redondo
                  }
                : rounded && aspectRatio === 'square'
                  ? {
                      width: previewHeight,
                      height: previewHeight,
                    }
                  : undefined
            }>
            <img
              src={coverImage.url}
              alt="Vista previa"
              className={clsx(
                'h-full w-full object-cover',
                rounded && 'rounded-full'
              )}
            />

            {/* Overlay con botones */}
            <div
              className={clsx(
                'absolute inset-0 flex items-center justify-center space-x-3 bg-black bg-opacity-50 opacity-0 transition-opacity hover:opacity-100',
                getRoundedClass()
              )}>
              <button
                type="button"
                onClick={handleRemoveImage}
                disabled={isMutating}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2">
                <Trash className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Área de carga
        <div className="flex justify-center">
          <div
            className={clsx(
              'cursor-pointer border-2 border-dashed text-center transition-colors',
              getRoundedClass(),
              isDragOver ? 'border-admin-blue bg-blue-50' : 'border-gray-300',
              isUploading && 'pointer-events-none opacity-50',
              aspectRatio !== 'free' && getAspectRatioClass(),
              // Centrar el área de carga cuando es redonda
              rounded && 'mx-auto',
              // Ajustar padding según el modo
              compactMode ? 'p-4' : 'p-8'
            )}
            style={
              aspectRatio === 'free'
                ? {
                    height: previewHeight,
                    width: rounded ? previewHeight : 'auto', // Hacer cuadrado cuando es redondo
                  }
                : rounded && aspectRatio === 'square'
                  ? {
                      width: previewHeight,
                      height: previewHeight,
                    }
                  : undefined
            }
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleSelectImage}>
            <div
              className={clsx(
                'flex h-full flex-col items-center justify-center',
                compactMode ? 'space-y-1' : 'space-y-3'
              )}>
              <svg
                className={clsx(
                  'text-gray-400',
                  // Ajustar el tamaño del icono según el modo y si es redondo
                  compactMode || (rounded && aspectRatio === 'square')
                    ? 'h-6 min-h-6 w-6'
                    : 'h-16 w-16'
                )}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true">
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="text-center">
                <p
                  className={clsx(
                    'font-medium text-gray-900',
                    // Ajustar el tamaño del texto según el modo
                    compactMode
                      ? 'text-xs'
                      : rounded && aspectRatio === 'square'
                        ? 'text-xs'
                        : 'text-sm'
                  )}>
                  {isUploading ? 'Subiendo...' : dragDropText}
                </p>
                {!compactMode && (
                  <>
                    <p
                      className={clsx(
                        'mt-1 text-gray-500',
                        rounded && aspectRatio === 'square'
                          ? 'text-xs'
                          : 'text-xs'
                      )}>
                      Arrastra una imagen aquí o haz clic para seleccionar
                    </p>
                    <p
                      className={clsx(
                        'mt-1 text-gray-500',
                        rounded && aspectRatio === 'square'
                          ? 'text-xs'
                          : 'text-xs'
                      )}>
                      {formatSupportText()}
                    </p>
                  </>
                )}
              </div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              accept={allowedExtensions.map((ext) => `.${ext}`).join(',')}
              disabled={isMutating || isUploading}
            />
          </div>
        </div>
      )}

      {cropOptions && (
        <ImageCropperModal
          file={fileToCrop}
          options={cropOptions}
          onConfirm={handleCropConfirm}
          onCancel={() => setFileToCrop(null)}
        />
      )}
    </div>
  )
}
